import { Component } from '@theme/component';
import { debounce, resetShimmer } from '@theme/utilities';
import { morphSection, sectionRenderer } from '@theme/section-renderer';
import { ThemeEvents, CartUpdateEvent, DiscountUpdateEvent } from '@theme/events';
import { freshCart, validSections, sectionContext, intendedQuantityConfirmed, lineIdentity } from './avl-cart-state.js';

/** Native cart lifecycle, with confirmed keyed writes and read-only recovery. */
class CartItemsComponent extends Component {
  #debouncedOnChange=debounce(intent=>this.updateQuantity(intent),200);
  #busy=false;
  #uncertain=false;
  #lock=null;
  #lastFocus=null;
  #lastScroll=0;
  connectedCallback(){
    super.connectedCallback();
    document.addEventListener(ThemeEvents.cartUpdate,this.#handleCartUpdate);
    document.addEventListener(ThemeEvents.discountUpdate,this.#handleCartUpdate);
    document.addEventListener(ThemeEvents.quantitySelectorUpdate,this.#onQuantityChange);
  }
  disconnectedCallback(){
    super.disconnectedCallback();
    document.removeEventListener(ThemeEvents.cartUpdate,this.#handleCartUpdate);
    document.removeEventListener(ThemeEvents.discountUpdate,this.#handleCartUpdate);
    document.removeEventListener(ThemeEvents.quantitySelectorUpdate,this.#onQuantityChange);
  }
  #onQuantityChange=(event)=>{
    if(!(event.target instanceof Node)||!this.contains(event.target))return;
    const row=event.target.closest?.('[data-key]');
    if(!row?.dataset.key)return;
    // Capture the intended line before another update can reorder the rows.
    this.#debouncedOnChange({key:row.dataset.key,identity:lineIdentity(this.#rowIdentity(row)),quantity:event.detail.quantity});
  };
  #rowIdentity(row){return {variant_id:row.dataset.variantId || row.querySelector('[data-variant-id]')?.dataset.variantId,properties:JSON.parse(row.dataset.properties||'{}'),selling_plan_allocation:{selling_plan:{id:row.dataset.planId || ''}}};}
  onLineItemRemove(line){return this.updateQuantity({line,quantity:0});}
  #status(message,error=false){
    let status=this.querySelector('[data-avlc-cart-status]');
    if(!status){status=document.createElement('div');status.dataset.avlcCartStatus='';status.className='avlc-cart__status';this.prepend(status);}
    status.hidden=!message;status.setAttribute('role',error?'alert':'status');status.textContent=message;
    return status;
  }
  #setDisabled(disabled){
    this.classList.toggle('cart-items-disabled',disabled);
    this.setAttribute('aria-busy',String(disabled));
    this.querySelectorAll('.avlc-cart__line-actions button,.avlc-cart__line-actions input,.cart-items__quantity button,.cart-items__quantity input,.cart__ctas button,cart-discount-component button,cart-discount-component input').forEach(control=>{
      if(disabled){if(!control.disabled){control.dataset.avlcWasEnabled='';control.disabled=true;}}
      else if(control.hasAttribute('data-avlc-was-enabled')){control.disabled=control.dataset.avlUnavailable==='true';control.removeAttribute('data-avlc-was-enabled');}
    });
    this.querySelectorAll('.additional-checkout-buttons').forEach(e=>{e.inert=disabled;});
    if(!disabled)this.querySelectorAll('cart-quantity-selector-component').forEach(e=>e.updateButtonStates?.());
  }
  #rememberFocus(row){
    const active=document.activeElement;
    this.#lastFocus={key:row.dataset.key,index:[...this.querySelectorAll('[data-key]')].indexOf(row),name:active?.getAttribute('name'),remove:active?.classList.contains('avlc-cart__remove')};
    this.#lastScroll=this.querySelector('.cart-drawer__content')?.scrollTop || 0;
  }
  #restoreFocus(){
    const state=this.#lastFocus;if(!state)return;
    if(this.isDrawer&&!this.closest('dialog')?.open){this.#lastFocus=null;return;}
    const rows=[...this.querySelectorAll('[data-key]')];
    const row=rows.find(e=>e.dataset.key===state.key)||rows[Math.min(state.index,rows.length-1)];
    const selector=state.remove?'.avlc-cart__remove':state.name?`[name="${CSS.escape(state.name)}"]`:'input';
    const preferred=row?.querySelector(selector);
    const target=preferred&&!preferred.disabled?preferred:row?.querySelector('button:not(:disabled)')||this.querySelector('[ref="closeButton"]')||this.querySelector('a[href]');
    if(target instanceof HTMLElement)target.focus({preventScroll:true});
    const scroll=this.querySelector('.cart-drawer__content');if(scroll)scroll.scrollTop=this.#lastScroll;
    this.#lastFocus=null;
  }
  async renderConfirmedCart(cart){
    if(!Array.isArray(cart.items)||!validSections(cart,[this.sectionId]))throw new Error('Cart sections unavailable');
    const html=cart.sections[this.sectionId];
    await morphSection(this.sectionId,html,{mode:this.isDrawer?'hydration':'full',injectStylesheet:this.isDrawer});
    this.updatedCallback();
    resetShimmer(this);
    this.#updateQuantitySelectors(cart);
  }
  async #apply(cart){
    await this.renderConfirmedCart(cart);
    this.#setDisabled(true);
    this.dispatchEvent(new CartUpdateEvent(cart,this.sectionId,{itemCount:cart.item_count,source:'cart-items-component',sections:cart.sections}));
  }
  async updateQuantity({line,key,identity,quantity}){
    if(!this.isConnected)return;
    const rows=[...this.querySelectorAll('[data-key]')];
    const row=key?rows.find(item=>item.dataset.key===key):rows[Number(line)-1];
    if(key&&(!row||lineIdentity(this.#rowIdentity(row))!==identity)){this.#status('Kurven er ændret. Vælg antallet igen.');return;}
    if(!row?.dataset.key || !Number.isInteger(Number(quantity)) || Number(quantity)<0)return;
    if((Number(quantity)>0&&(row.dataset.canUpdate==='false'||row.dataset.available==='false'))||(Number(quantity)===0&&row.dataset.canRemove==='false'))return;
    if(this.#busy||this.#uncertain||globalThis.__avlCartMutation){
      const input=row.querySelector('input');if(input)input.value=row.dataset.quantity || input.defaultValue;
      if(!this.#uncertain)this.#status('Kurven opdateres. Vent et øjeblik.');return;
    }
    quantity=Number(quantity);
    const original=this.#rowIdentity(row);
    this.#busy=true;this.#lock={};globalThis.__avlCartMutation=this.#lock;
    this.#rememberFocus(row);this.#setDisabled(true);this.#status('Opdaterer din kurv…');
    const ids=[this.sectionId];let confirmed=false;
    const controller=new AbortController();const timer=setTimeout(()=>controller.abort(),15000);
    try{
      const response=await fetch(Theme.routes.cart_change_url,{method:'POST',credentials:'same-origin',signal:controller.signal,headers:{'Content-Type':'application/json',Accept:'application/json'},body:JSON.stringify({id:row.dataset.key,quantity,sections:ids,sections_url:sectionContext({page:!this.isDrawer})})});
      const cart=await response.json();
      if(!response.ok||cart.errors||!Array.isArray(cart.items)||!validSections(cart,ids))throw new Error('Cart update needs reconciliation');
      await this.#apply(cart);confirmed=true;
      this.#reportIntent(cart,original,quantity);
    }catch(error){
      // A failed response can hide a successful write. Never repeat the POST.
      try{
        const cart=await freshCart(ids,{page:!this.isDrawer});await this.#apply(cart);confirmed=true;
        this.#reportIntent(cart,original,quantity);
      }catch{
        this.#uncertain=true;
        const status=this.#status('Vi kunne ikke bekræfte ændringen. Hent kurven igen, før du fortsætter.',true);
        const retry=document.createElement('button');retry.type='button';retry.className='button';retry.textContent='Hent kurven igen';retry.addEventListener('click',()=>this.retryCartRead());status.append(retry);
      }
    }finally{
      clearTimeout(timer);this.#busy=false;
      if(confirmed){this.#release();this.#setDisabled(false);this.#restoreFocus();}
    }
  }
  #reportIntent(cart,original,quantity){
    const success=intendedQuantityConfirmed(cart,original,quantity);
    this.#status(success?'Kurven er opdateret.':'Ændringen kunne ikke gennemføres som ønsket. Din aktuelle kurv er vist. Gennemgå antal og købsmåde.',!success);
  }
  #release(){if(globalThis.__avlCartMutation===this.#lock)delete globalThis.__avlCartMutation;this.#lock=null;this.#uncertain=false;}
  async retryCartRead(){
    if(this.#busy)return;this.#busy=true;this.#status('Henter din kurv…');
    try{const cart=await freshCart([this.sectionId],{page:!this.isDrawer});await this.#apply(cart);this.#release();this.#setDisabled(false);this.#status('Din aktuelle kurv er hentet.');this.#restoreFocus();}
    catch{const status=this.#status('Kurven kunne ikke hentes. Prøv igen, når forbindelsen er tilbage.',true);const retry=document.createElement('button');retry.type='button';retry.className='button';retry.textContent='Hent kurven igen';retry.addEventListener('click',()=>this.retryCartRead());status.append(retry);}
    finally{this.#busy=false;}
  }
  #handleCartUpdate=async(event)=>{
    if(event.target===this||this.#busy||this.#uncertain)return;
    if(event.detail?.data?.source==='cart-drawer-refresh'&&globalThis.__avlCartMutation)return;
    const html=event.detail?.data?.sections?.[this.sectionId] || event.detail?.resource?.sections?.[this.sectionId];
    try{
      if(html){await morphSection(this.sectionId,html,{mode:this.isDrawer?'hydration':'full',injectStylesheet:this.isDrawer});this.querySelectorAll('cart-quantity-selector-component').forEach(e=>e.updateButtonStates?.());}
      else if(event instanceof DiscountUpdateEvent && this.isDrawer){this.closest('cart-drawer-component')?.refreshCart();}
      else await sectionRenderer.renderSection(this.sectionId,{cache:false,mode:this.isDrawer?'hydration':'full'});
    }catch{this.#status('Kurven kunne ikke vises. Luk og åbn kurven igen, eller genindlæs siden.',true);}
  };
  #updateQuantitySelectors(cart){
    const totals=new Map();for(const item of cart.items||[])totals.set(String(item.variant_id),(totals.get(String(item.variant_id))||0)+item.quantity);
    document.querySelectorAll('quantity-selector-component[data-variant-id]').forEach(selector=>{const input=selector.querySelector('input[data-cart-quantity]');if(input){input.dataset.cartQuantity=String(totals.get(selector.dataset.variantId)||0);selector.updateCartQuantity?.();}});
  }
  get sectionId(){if(!this.dataset.sectionId)throw new Error('Section id missing');return this.dataset.sectionId;}
  get isDrawer(){return this.dataset.drawer!==undefined;}
}
if(!customElements.get('cart-items-component'))customElements.define('cart-items-component',CartItemsComponent);
